import React, { useContext, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactApexChart from "react-apexcharts";
import ChartModal from "./ChartModal"; // Assuming this modal handles chart type selection
import { fetchTaskPriorityCount } from "../../../../service/UserDashboard";
import AppContext from "../../../../context/AppContext";
import translations from "../../../common/Translations";
import { fetchFilterExcel } from "../../../../service/Task";
import { toast } from "../../../../utils/toast";
import Cookies from "js-cookie";
import Modal from "../../../DownloadModal/Modal";
import CommonButton from "../../../common/CommonButton";
import datePickerPopper from "../../../common/DatePickerPopper";
import ModalCloseButton from "../../../common/ModalCloseButton";
import ActionIconButton from "../../../common/ActionIconButton";
import { AlertCircle, BarChart3, CalendarDays, Download, Eye } from "lucide-react";

const parseYmdHm = (s) => {
  if (!s) return null;
  const [datePart, timePart = "00:00"] = String(s).split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return null;
  return new Date(y, m - 1, d, hh, mm, 0, 0);
};
const formatYmdHm = (date) => {
  if (!date) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const PriorityChart = ({ selectedWorkflowId, selectedOrgId }) => {
  const [userCount, setUserCount] = useState({});
  const [showModal, setShowModal] = useState(false);
  const { langMode, theme, projectId, activityId } = useContext(AppContext);
  const [chartView, setChartView] = useState("graph");
  const [downloadModal, setDownloadModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [priority, setPriority] = useState("Low_Priority");

  const today = new Date();
  const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59); // End of today

  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const startOfDayOneMonthAgo = new Date(oneMonthAgo.getFullYear(), oneMonthAgo.getMonth(), oneMonthAgo.getDate(), 0, 0, 0); // Start of day one month ago
  const [startDate, setStartDate] = useState(startOfDayOneMonthAgo.toISOString().substring(0, 16)); // YYYY-MM-DDTHH:MM
  const [endDate, setEndDate] = useState(endOfDay.toISOString().substring(0, 16)); // YYYY-MM-DDTHH:MM

  const [showDateModal, setShowDateModal] = useState(false);
  const todayPicker = useMemo(() => new Date(), []);

  const defaultColors = ["#6366F1", "#45B369", "#F59E0B", "#EF4444", "#8B5CF6"];
  const darkThemeColors = ["#60A5FA", "#34D399", "#FBBF24", "#F87171", "#A78BFA"];
  const highContrastColors = ["#FFFF00", "#00FFFF", "#FF00FF", "#00FF00", "#FFD700"];

  const getChartColors = () => {
    if (theme === "dark") return darkThemeColors;
    if (theme === "high-contrast") return highContrastColors;
    return defaultColors;
  };

  const formatLabel = (label) => {
    switch (label) {
      case "highPriority":
        return translations[langMode].highPriority;
      case "lowPriority":
        return translations[langMode].lowPriority;
      case "mediumPriority":
        return translations[langMode].mediumPriority;
      default:
        return label;
    }
  };

  const normalizePriorityCounts = (value = {}) => ({
    highPriority: Number(value.highPriority ?? value.highPriorityCount) || 0,
    mediumPriority: Number(value.mediumPriority ?? value.mediumPriorityCount) || 0,
    lowPriority: Number(value.lowPriority ?? value.lowPriorityCount) || 0,
  });

  const fetchData = async () => {
    try {
      const formattedStartDate = `${startDate}:00`;
      const formattedEndDate = `${endDate}:00`;

      const data = await fetchTaskPriorityCount(selectedWorkflowId, formattedStartDate, formattedEndDate);
      if (data && Object.keys(data).length > 0) {
        setUserCount(normalizePriorityCounts(data));
      } else {
        setUserCount({});
      }
    } catch (error) {
      console.error("Error fetching task priority count:", error);
      setUserCount({});
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedWorkflowId, startDate, endDate]);

  const categories = Object.keys(userCount).map(formatLabel);
  const data = categories.map(
    (category) =>
      userCount[
      Object.keys(userCount).find((key) => formatLabel(key) === category)
      ] || 0
  );

  const totalUserCount = data.reduce((sum, count) => sum + count, 0);
  const normalizedSeriesData = data.map(count =>
    totalUserCount > 0 ? (count / totalUserCount) * 100 : 0
  );

  const baseChartOptions = {
    chart: {
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      },
      foreColor: theme === "dark" ? "#E2E8F0" : theme === "high-contrast" ? "#FFF" : "#333",
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "12px",
        colors: [theme === "high-contrast" ? "#000" : (theme === "dark" ? "#FFF" : "#fff")],
      },
    },
    legend: {
      show: true,
      position: "bottom",
      fontSize: "13px",
      labels: {
        colors: theme === "dark" ? "#E2E8F0" : theme === "high-contrast" ? "#FFF" : "#333",
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5
      }
    },
    tooltip: {
      enabled: true,
      theme: theme === "dark" ? "dark" : "light",
      style: {
        fontSize: "13px",
        fontFamily: "inherit",
        background: theme === "dark" ? "#333" : theme === "high-contrast" ? "#FFF" : "#f4f4f4",
        color: theme === "dark" ? "#fff" : theme === "high-contrast" ? "#000" : "#333",
      },
      y: {
        formatter: function (val, { seriesIndex, dataPointIndex, w }) {
          if (w && w.config && w.config.chart && w.config.chart.type === 'radialBar') {
            return data[seriesIndex] + " users";
          }
          return val + " users";
        }
      }
    },
    colors: getChartColors(),
  };

  const RadialBarChartView = ({ series, labels }) => {
    const radialBarOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "radialBar",
        height: '100%',
      },
      plotOptions: {
        radialBar: {
          offsetY: 0,
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 5,
            size: '30%',
            background: 'transparent',
            image: undefined,
          },
          track: {
            show: true,
            startAngle: undefined,
            endAngle: undefined,
            background: theme === "dark" ? "#374151" : "#e0e0e0",
            strokeWidth: '97%',
            opacity: 1,
            margin: 10,
            dropShadow: {
              enabled: true,
              top: 2,
              left: 0,
              blur: 4,
              opacity: 0.15
            }
          },
          dataLabels: {
            show: true,
            name: {
              show: true,
              fontSize: '16px',
              fontFamily: 'MediumFont, Manrope, sans-serif',
              fontWeight: 600,
              color: baseChartOptions.chart.foreColor,
              offsetY: -10
            },
            value: {
              show: true,
              fontSize: '18px',
              fontFamily: 'MediumFont, Manrope, sans-serif',
              fontWeight: 700,
              color: baseChartOptions.chart.foreColor,
              offsetY: 5,
              formatter: function (val) {
                return `${Number.isFinite(val) ? val.toFixed(0) : 0}%`;
              }
            },
            total: {
              show: true,
              label: translations[langMode].priority || "Priority",
              color: baseChartOptions.chart.foreColor,
              formatter: function (w) {
                return data.reduce((a, b) => a + b, 0);
              }
            }
          }
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'horizontal',
          shadeIntensity: 0.5,
          gradientToColors: getChartColors(),
          inverseColors: true,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100]
        }
      },
      labels: labels,
      stroke: {
        lineCap: 'round'
      },
      legend: {
        show: true,
        position: 'bottom',
        offsetY: 0,
        height: 50,
        markers: {
          fillColors: getChartColors()
        }
      },
    };

    return (
      <div id="user-radial-bar-chart" className="w-full h-full �">
        <ReactApexChart
          options={radialBarOptions}
          series={series}
          type="radialBar"
          width="100%"
          height="100%"
        />
      </div>
    );
  };

  const DonutChartView = ({ series, labels }) => {
    const donutOptions = {
      ...baseChartOptions,
      labels: labels,
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "14px",
                color: baseChartOptions.chart.foreColor,
              },
              value: {
                show: true,
                fontSize: "18px",
                fontWeight: 600,
                color: baseChartOptions.chart.foreColor,
                formatter: function (val) {
                  return val;
                },
              },
              total: {
                show: true,
                showAlways: true,
                label: translations[langMode].total || "Total",
                fontSize: '14px',
                fontWeight: 600,
                color: baseChartOptions.chart.foreColor,
                formatter: function (w) {
                  return w.globals.seriesTotals.reduce((a, b) => {
                    return a + b;
                  }, 0);
                }
              },
            },
          },
        },
      },
      stroke: {
        show: true,
        colors: [theme === "dark" ? "#1F2937" : (theme === "high-contrast" ? "#000" : "#fff")],
        width: 2
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val + " tasks";
          }
        }
      }
    };

    return (
      <div id="chart" className="w-full h-full �">
        <ReactApexChart
          options={donutOptions}
          series={series}
          type="donut"
          width="100%"
          height="100%"
        />
      </div>
    );
  };

  const BarChartView = ({ series, categories }) => {
    
    const safeSeries = (series || []).map((v) => Number(v) || 0);
    const safeCategories = categories?.slice(0, safeSeries.length) || [];

    const barOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "bar",
        background: theme === "dark" ? "#1F2937" : "#FFFFFF",
      },
      xaxis: {
        categories: safeCategories,
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
            fontSize: '12px',
            fontWeight: 500,
          },
        },
        axisBorder: {
          show: true,
          color: theme === "dark" ? "#4A5568" : "#E2E8F0",
        },
        axisTicks: {
          show: true,
          color: theme === "dark" ? "#4A5568" : "#E2E8F0",
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
            fontSize: '13px',
            fontWeight: 500,
          },
          formatter: function (val) {
            return Math.floor(val);
          }
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        title: {
          text: translations[langMode].taskPriorityAxis || "Task Priority",
          style: {
            color: baseChartOptions.chart.foreColor,
            fontSize: '14px',
            fontWeight: 600,
          }
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 8,
          columnWidth: '45%',
          distributed: true,
          dataLabels: {
            position: 'top',
            offsetY: -20,
          },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -20,
        style: {
          fontSize: '13px',
          colors: [theme === "dark" ? "#FFF" : "#333"],
          fontWeight: 600,
        },
        background: {
          enabled: true,
          foreColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
          padding: 6,
          borderRadius: 4,
          borderWidth: 1,
          borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
          opacity: 0.9,
          dropShadow: {
            enabled: true,
            top: 1,
            left: 1,
            blur: 2,
            opacity: 0.2,
          }
        },
        formatter: function (val) {
          return val;
        }
      },
      grid: {
        show: true,
        borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
        strokeDashArray: 4,
        position: 'back',
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        },
        padding: {
          left: 0,
          right: 0
        }
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function (val) {
            return val + " users";
          }
        },
        x: {
          formatter: function (val) {
            return val;
          }
        },
      },
      colors: getChartColors(),
      legend: {
        show: false
      },
      responsive: [{
        breakpoint: 768,
        options: {
          plotOptions: {
            bar: {
              columnWidth: '60%',
            }
          },
          dataLabels: {
            offsetY: -15,
            style: {
              fontSize: '11px',
            }
          },
          xaxis: {
            labels: {
              style: {
                fontSize: '11px',
              }
            }
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '11px',
              }
            },
            title: {
              style: {
                fontSize: '12px',
              }
            }
          }
        }
      },
      {
        breakpoint: 480,
        options: {
          plotOptions: {
            bar: {
              columnWidth: '70%',
            }
          },
          dataLabels: {
            offsetY: -10,
            style: {
              fontSize: '10px',
            }
          },
          xaxis: {
            labels: {
              style: {
                fontSize: '10px',
              }
            }
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '10px',
              }
            },
            title: {
              style: {
                fontSize: '11px',
              }
            }
          }
        }
      }
      ]
    };

    return (
      <div id="user-bar-chart" className="w-full h-full">
        <ReactApexChart
          options={barOptions}
          series={[{ name: translations[langMode].priority || "Priority", data: safeSeries || [] }]}
          type="bar"
          width="100%"
          height="100%"
        />
      </div>
    );
  };

  const AreaChartView = ({ series, categories }) => {
    const areaOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "area",
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
            fontSize: '10px',
          },
        },
        tooltip: {
          enabled: false
        }
      },
      yaxis: {
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
          },
        },
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 90, 100]
        }
      },
      dataLabels: {
        enabled: true,
        background: {
          enabled: true,
          foreColor: theme === "high-contrast" ? "#FFF" : (theme === "dark" ? "#1F2937" : "#FFF"),
          padding: 4,
          borderRadius: 2,
          borderWidth: 1,
          borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
          opacity: 0.9,
        },
      },
      grid: {
        show: true,
        borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
        strokeDashArray: 4,
        position: 'back'
      }
    };

    return (
      <div id="area-chart" className="w-full h-full ">
        <ReactApexChart
          options={areaOptions}
          series={[{ name: translations[langMode].priorityCount || "Priority Count", data: series }]}
          type="area"
          width="100%"
          height="100%"
        />
      </div>
    );
  };

  const RadarChartView = ({ series, categories }) => {
    const radarOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "radar",
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
          },
        },
      },
      yaxis: {
        show: false,
      },
      stroke: {
        show: true,
        width: 2,
        curve: 'smooth',
        lineCap: 'round',
      },
      fill: {
        opacity: 0.4,
      },
      markers: {
        size: 5,
        hover: {
          size: 7,
        },
        colors: getChartColors(),
        strokeColors: theme === "dark" ? "#1F2937" : (theme === "high-contrast" ? "#000" : "#fff"),
        strokeWidth: 2,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          colors: [baseChartOptions.chart.foreColor]
        },
        background: {
          enabled: true,
          foreColor: theme === "high-contrast" ? "#FFF" : (theme === "dark" ? "#1F2937" : "#FFF"),
          padding: 4,
          borderRadius: 2,
          borderWidth: 1,
          borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
          opacity: 0.9,
        },
      },
      grid: {
        show: true,
        borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
        strokeDashArray: 4,
        position: 'back',
        xaxis: {
          lines: {
            show: true
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        },
        padding: {
          left: 20,
          right: 20
        }
      }
    };

    return (
      <div id="radar-chart" className="w-full h-full �">
        <ReactApexChart
          options={radarOptions}
          series={[{ name: translations[langMode].priorityCount || "Priority Count", data: series }]}
          type="radar"
          width="100%"
          height="100%"
        />
      </div>
    );
  };

  const isDataEmpty = Object.values(userCount).every(val => val === 0) || categories.length === 0;

  const handleShowModal = () => {
    if (!activityId) {
      toast.warn(translations[langMode].selectWorkflowFirst);
      return;
    }
    setDownloadModal(true);
  };

  const downloadFiltration = async (selectedOption) => {
    setIsLoading(true);
    try {
      const userTypeCookie = Cookies.get("userType");
      const params = {
        workflowIds: activityId || "",
        priority: priority,
        active: "",
        areaCode: "",
        accountNo: "",
        organizationId: "",
        projectId: projectId || ""
      };

      const orgId = JSON.parse(localStorage.getItem("selectedOrgId"))
      if (selectedOption === "all") {
        params.organizationId = orgId || "";
        params.workflowIds = "";
      } else if (
        selectedOption === "particular" ||
        userTypeCookie !== "super_admin"
      ) {
        params.organizationId = selectedOrgId || "";
        params.workflowIds = activityId || "";
      }

      const data = await fetchFilterExcel({
        workflowIds: params.workflowIds,
        priority: params.priority,
        active: params.active,
        areaCode: params.areaCode,
        accountNo: params.accountNo,
        organizationId: params.organizationId,
        projectId: params.projectId,
        includeProjectId: selectedOption === "all"
      });
      const code = data.header?.code;
      if (code === 600) {
        const s3Link = data.body?.value;
        const link = document.createElement("a");
        link.href = s3Link;
        link.setAttribute("download", "report.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(translations[langMode].excelisdownloading);
      } else {
        toast.error(data?.body?.value || translations[langMode].norecordfound);
      }
    } catch (error) {
      console.error("Error during report download:", error);
      toast.error(translations[langMode].anerroroccurred);
    } finally {
      setIsLoading(false);
    }
  };

  const isValidNumericArray = (arr) =>
  Array.isArray(arr) &&
  arr.length > 0 &&
  arr.every(v => Number.isFinite(Number(v)));

  const isChartDataInvalid =
  !isValidNumericArray(data) ||
  !Array.isArray(categories) ||
  categories.length !== data.length;


  return (
    <div className={`h-full w-full flex flex-col ${theme === "dark" ? "text-gray-200" : "text-gray-900"}`}>
      <div className="flex justify-between items-center py-1 sm:py-2 px-1 sm:px-2 w-full gap-2">
        <h2 className="font-semibold text-xs sm:text-base text-left flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
          <AlertCircle className="text-green-500 shrink-0" size={18} />
          <span className="truncate">{translations[langMode].priority}</span>
        </h2>

        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <ActionIconButton
            onClick={() => setShowDateModal(true)}
            tone="gray"
            className="text-gray-600 hover:text-gray-800"
            ariaLabel={translations[langMode].RangeFilter}
            tooltipText={translations[langMode].RangeFilter}
            tooltipPosition="bottom"
          >
            <CalendarDays size={18} />
          </ActionIconButton>
          <ActionIconButton
            type="button"
            onClick={handleShowModal}
            tone="gray"
            className="text-gray-500 hover:text-gray-700"
            aria-label={translations[langMode].download || "Download"}
            tooltipText={translations[langMode].download || "Download"}
            tooltipPosition="bottom"
          >
            <Download size={18} />
          </ActionIconButton>
          <ActionIconButton
            type="button"
            tone="gray"
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            onClick={() => setShowModal(true)}
            aria-label={translations[langMode].ViewChart}
            tooltipText={translations[langMode].ViewChart}
            tooltipPosition="bottom"
          >
            <Eye size={18} />
          </ActionIconButton>
        </div>

      </div>

      {(isDataEmpty || isChartDataInvalid) ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center px-4">
          <span
            className={`flex h-12 w-12 items-center justify-center rounded-full
              ${theme === "dark"
                ? "bg-[#236DB4]/20 text-[#236DB4]"
                : theme === "high-contrast"
                  ? "bg-black text-yellow-300"
                  : "bg-[#236DB4]/10 text-[#236DB4]"
              }`}
          >
            <BarChart3 className="h-6 w-6" />
          </span>
          <div className="flex flex-col gap-1">
            <p className={`text-sm font-medium ${theme === "high-contrast" ? "text-yellow-300" : theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
              {translations[langMode].noDataAvailable || translations[langMode].notchartYet}
            </p>
            <p className={`text-sm ${theme === "high-contrast" ? "text-yellow-200" : "text-gray-500"}`}>
              {translations[langMode].chartWaitingForResponses}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 w-full">
          {chartView === "chart" ? (
            <RadialBarChartView series={normalizedSeriesData} labels={categories} />
          ) : chartView === "donut" ? (
            <DonutChartView series={data} labels={categories} />
          ) : chartView === "area" ? (
            <AreaChartView series={data} categories={categories} />
          ) : chartView === "radar" ? (
            <RadarChartView series={data} categories={categories} />
          ) : (
            <BarChartView series={data} categories={categories} />
          )}
        </div>
      )}

      {showDateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div data-tooltip-bounds className={`relative max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800`}>
            <div className="mb-4 pr-8">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{translations[langMode].SelectDateTime}</h3>
            </div>
            <ModalCloseButton
              className="absolute right-4 top-4"
              onClick={() => setShowDateModal(false)}
            />
            <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="min-w-0">
              <label className="text-sm font-medium">{translations[langMode].startdatetime}</label>
              <DatePicker
                selected={parseYmdHm(startDate)}
                onChange={(date) => setStartDate(date ? formatYmdHm(date) : "")}
                placeholderText={translations[langMode].startdatetime}
                showTimeSelect
                timeIntervals={1}
                dateFormat="dd/MM/yyyy, HH:mm"
                maxDate={todayPicker}
                wrapperClassName="w-full"
                className={`w-full h-9 px-2 !rounded-lg text-sm border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""}`}
                calendarClassName="!rounded-lg shadow-lg"
                popperPlacement="bottom-start"
                popperContainer={datePickerPopper}
              />
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium">{translations[langMode].enddatetime}</label>
              <DatePicker
                selected={parseYmdHm(endDate)}
                onChange={(date) => setEndDate(date ? formatYmdHm(date) : "")}
                placeholderText={translations[langMode].enddatetime}
                showTimeSelect
                timeIntervals={1}
                dateFormat="dd/MM/yyyy, HH:mm"
                minDate={parseYmdHm(startDate)}
                maxDate={todayPicker}
                disabled={!startDate}
                wrapperClassName="w-full"
                className={`w-full h-9 px-2 !rounded-lg text-sm border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""}`}
                calendarClassName="!rounded-lg shadow-lg"
                popperPlacement="bottom-start"
                popperContainer={datePickerPopper}
              />
            </div>
            </div>
            <div className="flex justify-end gap-3">
              <CommonButton colorIntent="danger"
                onClick={() => setShowDateModal(false)}
                variant="danger"
                  tooltipText={translations[langMode].cancelandclose}
                  tooltipPosition="top"
              >
                {translations[langMode].cancle}
              </CommonButton>
              <CommonButton colorIntent="primary"
               tooltipText={translations[langMode].applytheselecteddaterange}
                tooltipPosition="top"
                onClick={() => {
                  const today = new Date();
                  const selectedEndDate = new Date(endDate);
                  if (selectedEndDate > today) {
                    toast.error("End date cannot be in the future.");
                    return;
                  }
                  if (new Date(startDate) > selectedEndDate) {
                    toast.error(translations[langMode].startdateerror);
                    return;
                  }
                  setShowDateModal(false);
                  // fetchData(); // redundant: date inputs call setStartDate/
                  // setEndDate directly, and the effect (deps:
                  // [selectedWorkflowId, startDate, endDate]) already
                  // re-fetches on that change.
                }}
                variant="primary"
              >
                {translations[langMode].apply}
              </CommonButton>
            </div>
          </div>
        </div>
      )}
      <ChartModal
        showModal={showModal}
        setShowModal={setShowModal}
        setChartView={setChartView}
        currentChartView={chartView}
        availableChartTypes={["radialBar", "bar", "area", "radar"]}
      />
      <Modal
        setDownloadModal={setDownloadModal}
        downloadModal={downloadModal}
        downloadFiltration={downloadFiltration}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        setPriority={setPriority}
        priority={priority}
        type="priority"
      />
    </div>
  );
};

export default PriorityChart;

